import { useAtom } from "jotai";
import { ResponseGetReportPowerBI } from "@CommonApi/report/powerbi";
import get from "es-toolkit/compat/get";
import isEmpty from "es-toolkit/compat/isEmpty";
import { IReportEmbedConfiguration, models, service } from "powerbi-client";
import { useMemo } from "react";
import { useLoaders } from "src/components/InlineLoader";
import { useToasts } from "src/components/Toasts";
import { powerBiReportsAtom } from "src/pages/merchandise/store/atoms";
import { Filter } from "src/pages/merchandise/store/types";

export type ReportData = ResponseGetReportPowerBI;

type Props = {
  reportId: string;
  filters?: Filter[];
};

const convertFilter = (filters: Filter[]) => {
  return filters.map((f) => ({
    $schema: "http://powerbi.com/product/schema#basic",
    target: {
      table: f.table,
      column: f.column,
    },
    operator: "In",
    values: f.value,
  }));
};
export const usePowerBiReport = ({ reportId, filters }: Props) => {
  const { addLoader, removeLoader } = useLoaders();
  const { toast } = useToasts();

  const [reportData] = useAtom(powerBiReportsAtom);

  const reportIndex =
    reportData?.embedUrl?.findIndex((report) => report.reportId === reportId) || 0;

  const reportConfig = useMemo(
    () =>
      isEmpty(reportData)
        ? null
        : ({
            type: "report",
            embedUrl: get(reportData, `embedUrl[${reportIndex}].embedUrl`, ""),
            tokenType: models.TokenType.Embed,
            accessToken: get(reportData, "accessToken", ""),
            ...(filters ? { filters: convertFilter(filters) } : {}),
          } as IReportEmbedConfiguration),
    [reportData, filters],
  );

  const eventHandlersMap = new Map([
    [
      "loaded",
      () => {
        addLoader("powerBiReportApi");
      },
    ],
    [
      "rendered",
      () => {
        removeLoader("powerBiReportApi");
      },
    ],
    [
      "error",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event?: service.ICustomEvent<any>) => {
        if (event) {
          toast({ title: "Powerbi Report failed", error: event.detail });
        }
      },
    ],
  ]);

  return { reportConfig, eventHandlersMap };
};
